/**
 * Peer Connection State Machine
 *
 * Defines states and allowed transitions for WebRTC peer connections.
 */

export const PEER_STATES = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed',
  CLOSED: 'closed',
};

/**
 * Allowed transitions.
 *
 * - CONNECTING → RECONNECTING: an attempt can fail before it ever connects
 *   (offer rejected, ICE failed) and must still be able to retry.
 * - FAILED / CLOSED → CONNECTING | RECONNECTING: a peer that gave up or was
 *   closed by the user can be connected again (manual connect, robot online,
 *   signaling server reconnected). Only `destroy()` is terminal.
 */
const TRANSITIONS = {
  [PEER_STATES.IDLE]:         [PEER_STATES.CONNECTING, PEER_STATES.CLOSED],
  [PEER_STATES.CONNECTING]:   [PEER_STATES.CONNECTED, PEER_STATES.RECONNECTING, PEER_STATES.FAILED, PEER_STATES.CLOSED],
  [PEER_STATES.CONNECTED]:    [PEER_STATES.RECONNECTING, PEER_STATES.CLOSED],
  [PEER_STATES.RECONNECTING]: [PEER_STATES.CONNECTING, PEER_STATES.FAILED, PEER_STATES.CLOSED],
  [PEER_STATES.FAILED]:       [PEER_STATES.CONNECTING, PEER_STATES.RECONNECTING, PEER_STATES.CLOSED],
  [PEER_STATES.CLOSED]:       [PEER_STATES.CONNECTING, PEER_STATES.RECONNECTING],
};

/**
 * Check if a state transition is valid.
 * @param {string} from - Current state
 * @param {string} to - Target state
 * @returns {boolean}
 */
export const isValidTransition = (from, to) => {
  const allowed = TRANSITIONS[from];
  return allowed ? allowed.includes(to) : false;
};

/**
 * Events exchanged between PeerConnection and ConnectionManager over the EventBus
 * (published as getRobotEvent(peerId, <event>)).
 */
export const PEER_INTERNAL_EVENTS = {
  RECONNECT_NEEDED: 'reconnect:needed',
  STATS_UPDATED: 'stats:updated',
  // per-peer audio settings changed: { peerId, microphoneEnabled, remoteAudioEnabled }
  MEDIA_CONFIG_CHANGED: 'media:config',
};

/**
 * Default data channel configurations.
 */
export const DEFAULT_DATA_CHANNELS_CONFIG = {
  chat: {
    ordered: true,
    maxPacketLifeTime: 100,
  },
};

/**
 * Default reconnection settings.
 */
export const RECONNECT_DEFAULTS = {
  maxRetries: 4,
  baseDelay: 3000,    // 3 seconds
  maxDelay: 30000,    // 30 seconds cap
  staggerDelay: 500,  // between peers in reconnectAll
};
